/*                                                            */
import { DEV } from 'esm-env';
import {
	active_reaction,
	active_effect,
	untracked_writes,
	get,
	set_untracked_writes,
	set_signal_status,
	untrack,
	increment_write_version,
	update_effect,
	current_sources,
	is_dirty,
	untracking,
	is_destroying_effect,
	push_reaction_value
} from '../runtime.js';
import { equals, safe_equals } from './equality.js';
import {
	CLEAN,
	DERIVED,
	DIRTY,
	BRANCH_EFFECT,
	INSPECT_EFFECT,
	UNOWNED,
	MAYBE_DIRTY,
	BLOCK_EFFECT,
	ROOT_EFFECT,
	ASYNC
} from '#client/constants';
import * as e from '../errors.js';
import { legacy_mode_flag, tracing_mode_flag } from '../../flags/index.js';
import { get_stack, tag_proxy } from '../dev/tracing.js';
import { component_context, is_runes } from '../context.js';
import { Batch, schedule_effect } from './batch.js';
import { proxy } from '../proxy.js';
import { execute_derived } from './deriveds.js';

export let inspect_effects = new Set();

/*                           */
export const old_values = new Map();

/**
 *
 */
export function set_inspect_effects(v) {
	inspect_effects = v;
}

let inspect_effects_deferred = false;

export function set_inspect_effects_deferred() {
	inspect_effects_deferred = true;
}

/**
 *
 *
 *
 *
 */
/*                                                  */
export function source(v, stack) {
	/*                */
	var signal = {
		f: 0, /*                                                                    */
		v,
		reactions: null,
		equals,
		rv: 0,
		wv: 0
	};

	if (DEV && tracing_mode_flag) {
		signal.created = stack ?? get_stack('CreatedAt');
		signal.updated = null;
		signal.set_during_effect = false;
		signal.trace = null;
	}

	return signal;
}

/**
 *
 *
 *
 */
/*                    */
export function state(v, stack) {
	const s = source(v, stack);

	push_reaction_value(s);

	return s;
}

/**
 *
 *
 *
 *
 */
/*                    */
export function mutable_source(initial_value, immutable = false, trackable = true) {
	const s = source(initial_value);
	if (!immutable) {
		s.equals = safe_equals;
	}

	/*                                                           */
	/*                                                          */
	if (legacy_mode_flag && trackable && component_context !== null && component_context.l !== null) {
		(component_context.l.s ??= []).push(s);
	}

	return s;
}

/**
 *
 *
 *
 */
export function mutate(source, value) {
	set(
		source,
		untrack(() => get(source))
	);
	return value;
}

/**
 *
 *
 *
 *
 *
 */
export function set(source, value, should_proxy = false) {
	if (
		active_reaction !== null &&
		/*                                                                                    */
		/*                                                          */
		(!untracking || (active_reaction.f & INSPECT_EFFECT) !== 0) &&
		is_runes() &&
		(active_reaction.f & (DERIVED | BLOCK_EFFECT | ASYNC | INSPECT_EFFECT)) !== 0 &&
		!current_sources?.includes(source)
	) {
		e.state_unsafe_mutation();
	}

	let new_value = should_proxy ? proxy(value) : value;

	if (DEV) {
		tag_proxy(new_value, /*                 */ (source.label));
	}

	return internal_set(source, new_value);
}

/**
 *
 *
 *
 *
 */
export function internal_set(source, value) {
	if (!source.equals(value)) {
		var old_value = source.v;

		if (is_destroying_effect) {
			old_values.set(source, value);
		} else {
			old_values.set(source, old_value);
		}

		source.v = value;

		var batch = Batch.ensure();
		batch.capture(source, old_value);

		if (DEV) {
			if (tracing_mode_flag || active_effect !== null) {
				const error = get_stack('UpdatedAt');

				if (error !== null) {
					source.updated ??= new Map();
					let entry = source.updated.get(error.stack);

					if (!entry) {
						entry = { error, count: 0 };
						source.updated.set(error.stack, entry);
					}

					entry.count++;
				}
			}

			if (active_effect !== null) {
				source.set_during_effect = true;
			}
		}

		if ((source.f & DERIVED) !== 0) {
			/*                                                                                                                             */
			if ((source.f & DIRTY) !== 0) {
				execute_derived(/*                  */ (source));
			}
			set_signal_status(source, (source.f & UNOWNED) === 0 ? CLEAN : MAYBE_DIRTY);
		}

		source.wv = increment_write_version();

		mark_reactions(source, DIRTY);

		/*                                                                             */
		/*                                                                                 */
		/*                                                                      */
		/*                                   */
		if (
			is_runes() &&
			active_effect !== null &&
			(active_effect.f & CLEAN) !== 0 &&
			(active_effect.f & (BRANCH_EFFECT | ROOT_EFFECT)) === 0
		) {
			if (untracked_writes === null) {
				set_untracked_writes([source]);
			} else {
				untracked_writes.push(source);
			}
		}

		if (DEV && inspect_effects.size > 0 && !inspect_effects_deferred) {
			flush_inspect_effects();
		}
	}

	return value;
}

export function flush_inspect_effects() {
	inspect_effects_deferred = false;

	const inspects = Array.from(inspect_effects);

	for (const effect of inspects) {
		/*                                                                       */
		/*                                                                   */
		if ((effect.f & CLEAN) !== 0) {
			set_signal_status(effect, MAYBE_DIRTY);
		}

		if (is_dirty(effect)) {
			update_effect(effect);
		}
	}

	inspect_effects.clear();
}

/**
 *
 *
 *
 *
 */
export function update(source, d = 1) {
	var value = get(source);
	var result = d === 1 ? value++ : value--;

	set(source, value);

	/*               */
	return result;
}

/**
 *
 *
 *
 *
 */
export function update_pre(source, d = 1) {
	var value = get(source);

	/*               */
	return set(source, d === 1 ? ++value : --value);
}

/**
 *
 *
 */
export function increment(source) {
	set(source, source.v + 1);
}

/**
 *
 *
 *
 */
function mark_reactions(signal, status) {
	var reactions = signal.reactions;
	if (reactions === null) return;

	var runes = is_runes();
	var length = reactions.length;

	for (var i = 0; i < length; i++) {
		var reaction = reactions[i];
		var flags = reaction.f;

		/*                                                                */
		if (!runes && reaction === active_effect) continue;

		/*                                                                           */
		if (DEV && (flags & INSPECT_EFFECT) !== 0) {
			inspect_effects.add(reaction);
			continue;
		}

		var not_dirty = (flags & DIRTY) === 0;

		/*                                        */
		if (not_dirty) {
			set_signal_status(reaction, status);
		}

		if ((flags & DERIVED) !== 0) {
			mark_reactions(/*                  */ (reaction), MAYBE_DIRTY);
		} else if (not_dirty) {
			schedule_effect(/*                 */ (reaction));
		}
	}
}
